import React from "react";
import { Authenticated, Unauthenticated } from "convex/react";
import { Toaster } from "sonner";
import { SimplifiedAddReferenceForm } from "./pages/add-reference/components/SimplifiedAddReferenceForm";
import type { Reference } from "./shared/types/reference";
import { PWAUpdatePrompt } from "./shared/ui/components/PWAUpdatePrompt";
import { ShareTargetHandler } from "./app/components/ShareTargetHandler";
import { DebugLogs } from "./pages/debug/components/DebugLogs";
import { UnifiedSearchPage } from "./pages/search/components/UnifiedSearchPage";
import { ReferencePage } from "./pages/reference-detail/components/ReferencePage";
// Lazy load heavy components
const SavedPromptsManager = React.lazy(() => import("./pages/saved-prompts/components/SavedPromptsManager").then(module => ({ default: module.SavedPromptsManager })));
const TokenManagementPage = React.lazy(() => import("./pages/tokens/components/TokenManagementPage").then(module => ({ default: module.TokenManagementPage })));
const AdminTokenManagement = React.lazy(() => import("./pages/tokens/components/AdminTokenManagement").then(module => ({ default: module.AdminTokenManagement })));
const TokenAnalytics = React.lazy(() => import("./pages/tokens/components/TokenAnalytics").then(module => ({ default: module.TokenAnalytics })));
import { SearchProvider } from "./shared/contexts/SearchContext";
import { useGlobalMediaHandlers } from "./pages/add-reference/hooks/useGlobalMediaHandlers";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useState, useCallback, useEffect } from "react";
import type { Doc } from "../convex/_generated/dataModel";
import { LoadingSpinner } from './shared/ui/components/LoadingSpinner';
import { LandingPage } from './pages/landing/components/LandingPage';
import { TagWordCloud } from './pages/tag-cloud/components/TagWordCloud';
import { AppLayout } from './shared/layout/layout/AppLayout';
import { ReferenceDataProvider } from "./shared/contexts/ReferenceDataContext";
import { FullscreenViewerProvider } from "./shared/contexts/FullscreenViewerContext";
import { FullscreenViewer } from "./shared/ui/components/FullscreenViewer";
import { PageContainer } from "./shared/layout/layout/PageContainer";
import { PublicDirectoryPage } from './pages/public-directory/components/PublicDirectoryPage';
import { PublicReferencePage } from './pages/public-directory/components/PublicReferencePage';
import { ModerationPage } from './pages/moderation/components/ModerationPage';
import { ProjectsListPage } from './pages/projects/components/ProjectsListPage';
import { ProjectWorkspacePage } from './pages/projects/components/ProjectWorkspacePage';
import { SettingsPage } from './pages/settings/components/SettingsPage';
// Enhanced keyboard shortcuts hook
function useKeyboardShortcuts() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only trigger when no input/textarea is focused and no modifiers except specified ones
      const activeElement = document.activeElement;
      const isInputFocused = activeElement?.tagName === 'INPUT' || 
                            activeElement?.tagName === 'TEXTAREA' || 
                            activeElement?.getAttribute('contenteditable') === 'true';
      
      if (isInputFocused) return;

      // Safe shortcuts without Ctrl to avoid browser conflicts
      switch (e.key) {
        case 'a':
          if (!e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            void navigate('/new-reference');
          }
          break;
        case 'h':
          if (!e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            void navigate('/');
          }
          break;
        case '/':
          if (!e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            // Focus the search input if we're on the main page
            if (location.pathname === '/') {
              const searchInput = document.querySelector('input[placeholder*="search" i]') as HTMLInputElement;
              if (searchInput) {
                searchInput.focus();
              }
            } else {
              void navigate('/');
            }
          }
          break;
        case 't':
          if (!e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            void navigate('/tag-cloud');
          }
          break;
         case 'p':
           if (!e.ctrlKey && !e.metaKey && !e.altKey) {
             e.preventDefault();
             void navigate('/saved-prompts');
           }
           break;
         case 'd':
           if (!e.ctrlKey && !e.metaKey && !e.altKey) {
             e.preventDefault();
             void navigate('/public');
           }
           break;
         case 'n':
           if (!e.ctrlKey && !e.metaKey && !e.altKey) {
             e.preventDefault();
             void navigate('/projects');
           }
           break;
        case 'Escape':
          // Universal escape to home
          if (location.pathname !== '/') {
            e.preventDefault();
            void navigate('/');
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate, location.pathname]);
}

function AuthenticatedApp() {
  const [editingReference, setEditingReference] = useState<Reference | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  
  // Use keyboard shortcuts
  useKeyboardShortcuts();
  
  // Use global media handlers for paste and drag-drop
  useGlobalMediaHandlers();

  const handleFormClose = useCallback(() => {
    setEditingReference(null);
    void navigate('/');
  }, [navigate]);

  const handleEditReference = useCallback((ref: Doc<"references">) => {
    setIsLoading(true);
    try {
      // Convert ReferenceDoc to Reference
      const reference: Reference = {
        _id: ref._id,
        title: ref.title,
        type: ref.type,
        description: ref.description,
        sourceURL: ref.sourceURL,
        category: ref.category,
        storageId: ref.storageId
      };
      setEditingReference(reference);
      void navigate('/new-reference');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  return (
    <SearchProvider>
      <Routes>
        <Route path="/landing" element={
          <div className="animate-in fade-in-0 duration-200">
            <LandingPage />
          </div>
        } />
        <Route path="*" element={
          <AppLayout fullWidth={true}>
            {isLoading && <LoadingSpinner />}
            <Routes>
              <Route path="/new-reference" element={
                <PageContainer 
                  title="Add Reference"
                  description="Add a new reference to your collection"
                  className="animate-in fade-in-0 duration-200"
                  fullWidth={true}
                >
                  <SimplifiedAddReferenceForm 
                    existingReference={editingReference} 
                    onFormClose={handleFormClose}
                  />
                </PageContainer>
              } />
              <Route path="/reference/:id" element={
                <div className="animate-in fade-in-0 duration-200">
                  <ReferencePage onEditReference={handleEditReference} />
                </div>
              } />
              <Route path="/debug" element={
                <PageContainer 
                  title="Debug Logs"
                  description="View application debug information and logs"
                  className="animate-in fade-in-0 duration-200"
                  fullWidth={true}
                >
                  <DebugLogs />
                </PageContainer>
              } />
              <Route path="/tag-cloud" element={
                <PageContainer 
                  title="TAG CLOUD"
                  description="Explore your references by tags - larger tags appear more frequently. Click on any tag to filter your references!"
                  className="animate-in fade-in-0 duration-200"
                  fullWidth={true}
                >
                  <TagWordCloud 
                    onTagSelect={(tagId, tagName) => {
                      console.log('Selected tag:', tagName, tagId);
                    }}
                  />
                </PageContainer>
              } />
              <Route path="/saved-prompts" element={
                <PageContainer 
                  title="Saved AI Prompts"
                  description="View and manage your saved AI prompts generated from references"
                  className="animate-in fade-in-0 duration-200"
                  fullWidth={true}
                >
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <React.Suspense fallback={<LoadingSpinner />}>
                      <SavedPromptsManager isOpen={true} embedded={true} />
                    </React.Suspense>
                  </div>
                </PageContainer>
              } />
              <Route path="/tokens" element={
                <PageContainer 
                  title="Token Management"
                  description="Manage your API tokens and usage"
                  className="animate-in fade-in-0 duration-200"
                  fullWidth={true}
                >
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <TokenManagementPage />
                  </React.Suspense>
                </PageContainer>
              } />
              <Route path="/admin/tokens" element={
                <PageContainer 
                  title="Admin Token Management"
                  description="Administrative token management and user overview"
                  className="animate-in fade-in-0 duration-200"
                  fullWidth={true}
                >
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <AdminTokenManagement />
                  </React.Suspense>
                </PageContainer>
              } />
               <Route path="/admin/analytics" element={
                <PageContainer 
                  title="Token Analytics"
                  description="View token usage analytics and system metrics"
                  className="animate-in fade-in-0 duration-200"
                  fullWidth={true}
                >
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <TokenAnalytics />
                  </React.Suspense>
                </PageContainer>
              } />
              <Route path="/public" element={
                <div className="animate-in fade-in-0 duration-200">
                  <PublicDirectoryPage />
                </div>
              } />
              <Route path="/admin/moderation" element={
                <PageContainer
                  title="Moderation Queue"
                  description="Review and approve pending references for public display"
                  className="animate-in fade-in-0 duration-200"
                  fullWidth={true}
                >
                  <ModerationPage />
                </PageContainer>
              } />
              <Route path="/projects" element={
                <PageContainer
                  className="animate-in fade-in-0 duration-200"
                  fullWidth={true}
                >
                  <ProjectsListPage />
                </PageContainer>
              } />
              <Route path="/projects/:projectId" element={
                <div className="animate-in fade-in-0 duration-200">
                  <ProjectWorkspacePage />
                </div>
              } />
              <Route path="/settings" element={
                <PageContainer
                  title="Settings"
                  description="Manage your account settings and API tokens"
                  className="animate-in fade-in-0 duration-200"
                  fullWidth={true}
                >
                  <SettingsPage />
                </PageContainer>
              } />
              <Route path="/" element={
                <PageContainer 
                  className="animate-in fade-in-0 duration-200"
                  fullWidth={true}
                >
                  <UnifiedSearchPage onEditReference={handleEditReference} />
                </PageContainer>
              } />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AppLayout>
        } />
      </Routes>
    </SearchProvider>
  );
}

export default function App() {
  return (
    <Router>
        <ReferenceDataProvider>
        <FullscreenViewerProvider>
          <div className="app-container">
          <Routes>
            {/* Handle share target before authentication */}
            <Route path="/share-target" element={<ShareTargetHandler />} />
            {/* Public directory - accessible to everyone */}
            <Route path="/public" element={
              <div className="animate-in fade-in-0 duration-200">
                <PublicDirectoryPage />
              </div>
            } />
            <Route path="/public/reference/:id" element={
              <div className="animate-in fade-in-0 duration-200">
                <PublicReferencePage />
              </div>
            } />
            <Route path="/*" element={
              <>
                <Authenticated>
                  <AuthenticatedApp />
                </Authenticated>
                <Unauthenticated>
                  <main className="flex-1 p-4 md:p-0">
                    <div className="mx-auto">
                      <div className="animate-in fade-in-0 duration-200">
                        <LandingPage />
                      </div>
                    </div>
                  </main>
                </Unauthenticated>
              </>
            } />
          </Routes>
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: 'white',
                color: '#1e293b',
                border: '1px solid #e2e8f0',
                borderRadius: '0.5rem',
              },
            }}
          />
          <PWAUpdatePrompt />
        </div>
        <FullscreenViewer />
      </FullscreenViewerProvider>
      </ReferenceDataProvider>
    </Router>
  );
}
